<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { type LocationQuery, useRoute, useRouter } from 'vue-router';
import { debounce } from 'radash';
import ProjectSearchFiltersDialog from '@/components/Shared/Projects/ProjectSearchFiltersDialog.vue';
import FilterLinesSvg from '@/assets/svg/filter-lines-20.svg?component';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { isAbortError } from '@/core/shared/errors/abort-error.util';
import MeService from '@/core/shared/me/me.service';
import ProjectService from '@/core/shared/project/project.service';
import { ProjectFilterTab } from '@/core/shared/project/project.type';

const emit = defineEmits<{
  (e: 'change:filters'): void;
  (e: 'initial-search:done'): void;
}>();

const meService = new MeService();
const projectService = new ProjectService();
const route = useRoute();
const router = useRouter();
const isProjectSearchFiltersDialogOpen = ref<boolean>(false);
const searchQuery = computed(() => route.query);
const filtersCount = computed(() => projectService.filtersCount);
const titleOrReqIdInput = ref<HTMLInputElement | null>(null);

async function setTitleOrReqIdQuery() {
  const searchText = titleOrReqIdInput.value;
  const query = { ...searchQuery.value };

  if (searchText?.value) {
    query.titleOrReqId = searchText.value;
  } else {
    delete query.titleOrReqId;
  }
  // Since we moved the toggle here, we need to save the copilot value to the query
  // or remove it if it's not selected
  if (projectService.copilot) {
    query.copilot = String(projectService.copilot);
  } else {
    delete query.copilot;
  }

  if (projectService.locations?.length) {
    query.locations = projectService.locations;
  }
  if (projectService.status) {
    query.status = projectService.status;
  }
  if (projectService.teamIdsQuery) {
    query.teamIds = projectService.teamIdsQuery;
  } else {
    delete query.teamIds;
  }
  // Explicit, because All jobs is otherwise only the absence of `teamIds`.
  // `jobsTab`, not `tab`: `tab` is the candidate-pane deep-link key.
  query.jobsTab = projectService.selectedTab;
  if (projectService.source) {
    query.source = projectService.source;
  }

  await router.push({ query });
}

function openProjectSearchFiltersDialog() {
  isProjectSearchFiltersDialogOpen.value = true;
}

/**
 * Sets the project filters based on the query from the URL
 * @param storageQueryData if provided, will use this data to set the project filters instead of the query from the URL
 */
function setProjectFilters(localStorageQuery?: LocationQuery | null) {
  projectService.applyFilterQuery(localStorageQuery ?? searchQuery.value);
  projectService.saveFilterQuery();

  emit('change:filters');
}

async function selectMyJobs() {
  projectService.selectedTab = ProjectFilterTab.MY_JOBS;
  // Set the current user as the only team member
  const ownUserProfileId = meService.userProfile?.id as number;
  projectService.teamIds = [ownUserProfileId];
  projectService.didSelectUserInProjectFilters = true;
  // The query watcher is the single trigger, so one click is one search.
  await setTitleOrReqIdQuery();
}

async function selectAllJobs() {
  projectService.selectedTab = ProjectFilterTab.ALL_JOBS;
  // Clear team filter when switching to All Jobs
  projectService.teamIds = [];
  projectService.didSelectUserInProjectFilters = true;
  await setTitleOrReqIdQuery();
}

function toggleCopilotFilter() {
  projectService.copilot = projectService.copilot === true ? false : true;
  setTitleOrReqIdQuery();
  emit('change:filters');
}

const debouncedSetTitleOrReqIdQuery = debounce({ delay: 500 }, setTitleOrReqIdQuery);

watch(searchQuery, () => {
  setProjectFilters();
});

onMounted(async () => {
  const mountedPath = route.path;

  // Loading the location dropdown and resolving the saved filter are
  // independent: a failure in one must not drop the other, and a slow
  // locations request must not delay the list. Mirrors #1472.
  const [locationsResult, initialSearchResult] = await Promise.allSettled([
    projectService.getEmployerLocations(),
    // The service owns the first resolved search, so the landing route and this
    // bar share one request instead of racing an unfiltered one.
    projectService.ensureInitialSearch({
      query: route.query,
      onFilterResolved: (resolvedQuery) => {
        // A slow first search can resolve after the user has left; the replace
        // would then rewrite the query of whatever page they moved on to.
        if (route.path !== mountedPath) return;
        return router.replace({ query: { ...route.query, ...resolvedQuery } });
      },
    }),
  ]);

  if (locationsResult.status === 'rejected' && !isAbortError(locationsResult.reason)) {
    ErrorService.captureException(locationsResult.reason);
    SnackbarService.critical('Failed to load location options');
  }

  if (initialSearchResult.status === 'rejected' && !isAbortError(initialSearchResult.reason)) {
    ErrorService.captureException(initialSearchResult.reason);
  }

  // The service owns the first search, but its side effects live in the bar:
  // a URL-filter load resolves without a query change, so nothing else fires.
  emit('initial-search:done');
});
</script>

<template>
  <div>
    <!-- Title Input -->
    <div
      class="relative flex h-[40px] items-center rounded-md bg-shade-840 p-2"
      data-test-id="project-filter-section"
    >
      <input
        ref="titleOrReqIdInput"
        v-model="projectService.titleOrReqId"
        type="text"
        placeholder="Search by job name or ATS Req ID"
        class="flex-1 bg-shade-840 text-sm focus:outline-none"
        @input="debouncedSetTitleOrReqIdQuery"
        data-test-id="project-search-text-filter"
      />
    </div>

    <!-- Tab Selection: My Jobs / All Jobs -->
    <div class="mt-2 flex rounded-md bg-shade-840 p-0.5">
      <button
        class="tab-filter-button"
        :class="{
          selected: projectService.selectedTab === ProjectFilterTab.MY_JOBS,
        }"
        data-test="jobs-tab-my-jobs"
        @click="selectMyJobs"
      >
        My jobs
      </button>
      <button
        class="tab-filter-button"
        :class="{
          selected: projectService.selectedTab === ProjectFilterTab.ALL_JOBS,
        }"
        data-test="jobs-tab-all-jobs"
        @click="selectAllJobs"
      >
        All jobs
      </button>
    </div>

    <div class="mt-2 flex justify-between">
      <!-- Filters Menu Button -->
      <button
        class="inline-flex items-center rounded-md px-2 py-1 transition-colors duration-300 hover:bg-shade-840"
        @click="openProjectSearchFiltersDialog"
      >
        <!-- Label -->
        <div class="text-xs font-semibold leading-4">Filters</div>
        <!-- Icon w/ Count -->
        <div class="relative ml-1 cursor-pointer">
          <FilterLinesSvg class="stroke-white" />
          <div
            v-if="filtersCount"
            class="absolute right-[-4px] top-[-2px] h-[12px] w-[12px] rounded-full bg-highlight-600"
          >
            <div class="flex items-center justify-center">
              <span class="m-[-1.5px] text-2xs font-bold">{{ filtersCount }}</span>
            </div>
          </div>
        </div>
      </button>
      <!-- Copilot Filter Toggle -->
      <div class="mb-1.5 mt-2 flex items-center">
        <label class="flex cursor-pointer items-center">
          <span class="mr-3.5 text-xs">Show active jobs only</span>
          <v-switch
            v-model:model-value="projectService.copilot"
            color="highlight-500"
            class="mr-2 !inline-block"
            hide-details
            density="compact"
            @click="toggleCopilotFilter"
          />
        </label>
      </div>
    </div>

    <ProjectSearchFiltersDialog v-model="isProjectSearchFiltersDialogOpen" />
  </div>
</template>
<style lang="postcss" scoped>
.tab-filter-button {
  @apply flex-1 rounded-md py-2.5 text-xs leading-4 text-tint-200;
  @apply transition-all duration-300;
  &:hover {
    @apply bg-shade-820 text-tint-0;
  }
  &.selected {
    @apply bg-tint-0 font-semibold text-shade-900;
  }
}
:deep(.v-switch) {
  @apply mt-[-20px] h-5;
  &__track {
    opacity: 1;
    @apply bg-tint-400;
  }
  &__track.bg-highlight-500 {
    opacity: 1;
  }
  &__thumb {
    @apply border border-tint-400 !bg-white;
  }
}
</style>
